import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/Button";
import { AuthModal } from "@/components/auth/AuthModal";
import { useAuth } from '@/lib/auth/store';
import { Instagram, Phone, Music, MapPin } from "lucide-react";
import { MetaTags } from "@/components/MetaTags";
import { useLocation } from "react-router-dom";

export function Lucido() {
  const { user } = useAuth();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Initialize Google Maps
  useEffect(() => {
    const mapElement = document.getElementById("venue-map");
    if (!mapElement || !window.google?.maps) return;

    const location = { lat: 25.7947, lng: -80.134 };
    const map = new google.maps.Map(mapElement, {
      center: location,
      zoom: 14,
      mapTypeId: "satellite",
      disableDefaultUI: true,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM,
        style: google.maps.ZoomControlStyle.SMALL,
      },
    });

    new google.maps.Marker({
      position: location,
      map,
      title: "Miami Beach Botanical Garden - 2000 Convention Center Drive",
    });
  }, []);

  const handleButtonClick = (url: string) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    window.open(url, "_blank");
  };

  return (
    <div className="relative min-h-screen bg-earth-900">
      <MetaTags
        title="Lúcido Miami 2nd Edition - rendezvous"
        description="Join us for an exclusive evening of art and culture in Miami."
        image="https://rendezvouswebsite.b-cdn.net/Lucido/botanical-garden-preview.jpg"
        url="https://letsrendezvous.co/lucido-april-12"
      />

      {/* Hero Section */}
      <div className="relative min-h-screen w-full">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://rendezvouswebsite.b-cdn.net/Lucido/botanical-garden-preview.jpg"
            alt="Lucido Miami"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-earth-900/50 to-earth-900" />
        </div>

        {/* Content */}
        <div className="absolute inset-0 flex items-center px-4 pt-[50vh]">
          <div className="container mx-auto">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-center space-y-8"
              >
                {/* Main Headers */}
                <div>
                  <h1 className="text-5xl sm:text-7xl md:text-8xl lg:text-[8rem] font-body text-sand-100 tracking-[0.2em] leading-none mb-8">
                    LÚCIDO
                  </h1>
                  <p className="text-xl sm:text-3xl md:text-4xl font-body text-sand-100 tracking-[0.2em] mb-8">
                    MIAMI
                  </p>
                  <p className="text-lg sm:text-2xl md:text-3xl text-sand-200 font-body tracking-[0.05em]">
                    SATURDAY, APRIL 12TH
                  </p>
                </div>

                {/* Event Description */}
                <div className="bg-earth-800/50 p-4 sm:p-6 rounded-lg max-w-2xl mx-auto text-justify">
                  <p className="text-sand-300 text-sm sm:text-base leading-relaxed mb-6">
                    Whether or not you joined us last time at BeyBey or Soho
                    House, we're excited to welcome you to the second Miami
                    edition of CDMX-born fiesta Lúcido, a curated music series
                    bringing back quality sounds and vinyl with an international
                    ambience. This time we are taking over the magical Botanical
                    Garden of South Beach from 5 pm onwards. We'll start with
                    natural wines, an elixir bar and non-alcoholic options such
                    as aqua de Jamaica and sueros, including other surprises.
                  </p>

                  <p className="text-sand-300 text-sm sm:text-base leading-relaxed mb-6">
                    We have a line up we can't announce but that includes both a
                    Dante's and a DISTRIKT resident so expect housy, groovy
                    tunes and not your typical Miami crowd.
                  </p>
                  <p className="text-sand-300 text-sm sm:text-base leading-relaxed mb-6">
                    DM Lúcido via{" "}
                    <a
                      href="mailto:Lucido.community@gmail.com"
                      className="text-sand-300 hover:text-sand-200 transition-colors underline font-medium"
                    >
                      email
                    </a>{" "}
                    or{" "}
                    <a
                      href="https://www.instagram.com/lucido._____/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sand-300 hover:text-sand-200 transition-colors underline font-medium"
                    >
                      instagram
                    </a>{" "}
                    for tables powered by Kimia Mezcal.
                  </p>
                  <p className="text-sand-300 text-sm sm:text-base leading-relaxed">
                    {" "}
                    Dress code (optional): silver glam, metallic, light colors
                  </p>
                </div>

                {/* Social Links */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
                  <a
                    href="https://soundcloud.com/lucido-community"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sand-300 hover:text-sand-200 transition-colors text-sm sm:text-base"
                  >
                    <Music className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span>Listen</span>
                  </a>
                  <a
                    href="https://www.instagram.com/lucido._____/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sand-300 hover:text-sand-200 transition-colors text-sm sm:text-base"
                  >
                    <Instagram className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span>Stay in touch via Instagram</span>
                  </a>
                </div>

                {/* Tickets Section - Greyed Out */}
                <div className="max-w-lg mx-auto space-y-3">
                  <div className="bg-earth-800/50 p-4 rounded-lg">
                    <p className="text-sand-400 text-sm sm:text-base tracking-[0.1em] font-body">
                      EARLY BIRD - SOLD OUT
                    </p>
                  </div>

                  <div className="bg-earth-800/50 p-4 rounded-lg">
                    <p className="text-sand-400 text-sm sm:text-base tracking-[0.1em] font-body">
                      EVENT COMPLETED - APRIL 12, 2024
                    </p>
                  </div>

                  <Button 
                    size="lg"
                    disabled={true}
                    className="w-full text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4 tracking-[0.1em] font-body bg-earth-700/50 text-sand-500"
                  >
                    TICKETS NO LONGER AVAILABLE
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Location Section */}
      <div className="bg-earth-900 py-96">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto space-y-4">
            <div className="flex items-center justify-center gap-2 text-sand-300 mb-8">
              <MapPin className="h-5 w-5" />
              <a
                href="https://maps.google.com/?q=Miami+Beach+Botanical+Garden,+2000+Convention+Center+Dr,+Miami+Beach,+FL+33139"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-sand-200 transition-colors"
              >
                Miami Beach Botanical Garden - 2000 Convention Center Drive
              </a>
            </div>
            <div
              id="venue-map"
              className="w-full h-[300px] rounded-lg overflow-hidden"
            />
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultTab="sign-up"
        redirectTo={location.pathname}
      />
    </div>
  );
}